import { emitFromApiError } from '@/lib/auth/sessionCoordinator';
import { normalizeApiErrorDetail } from '@/lib/normalizeApiError';
import { toApiErrorPayload, type ApiErrorPayload } from '@/lib/api';

export type ApiError = ApiErrorPayload & {
  messageVN: string;
  source?: string;
  at: number;
};

type MinimalApiResponse = {
  ok: boolean;
  status: number;
  data?: unknown;
};

export function toApiError(errorOrMessage: unknown, source?: string): ApiError {
  const payload = toApiErrorPayload(errorOrMessage);
  return {
    ...payload,
    messageVN: payload.message,
    source,
    at: Date.now(),
  };
}

export function throwApiErrorFromResponse(
  response: MinimalApiResponse,
  fallbackMessage: string,
  source: string
): never {
  const apiError = toApiError(
    {
      status: response.status,
      ...(typeof response.data === 'object' && response.data !== null ? response.data : { message: fallbackMessage }),
      message:
        (response.data as { message?: string } | undefined)?.message ??
        fallbackMessage,
    },
    source
  );

  emitFromApiError(apiError, source);
  throw apiError;
}

export function shouldRetryByErrorKind(failureCount: number, error: unknown): boolean {
  const normalized = normalizeApiErrorDetail(error);
  if (normalized.kind === 'auth' || normalized.kind === 'validation') {
    return false;
  }
  if (normalized.kind === 'network') {
    return failureCount < 3;
  }
  if (normalized.kind === 'server') {
    return failureCount < 2;
  }
  return failureCount < 2;
}

export function retryDelayByErrorKind(attempt: number): number {
  const base = 1000;
  return Math.min(base * 2 ** Math.max(0, attempt - 1), 8000);
}

